import { atom, DefaultValue, useRecoilCallback } from 'recoil';
import type { AtomEffect, RecoilState } from 'recoil';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { broadcastGenerationCommit } from 'modules/viewer/workers/workerSelfRetirement';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { adoptGeneration, proposeBump, type GenerationState } from './policy';
import { TAB_ID } from './tabId';
import { readEscalation, writeEscalation, clearEscalation } from './escalationStore';

// VX-4302: announce a committed bump to the live workers so the superseded generation self-retires
// (the no-zombie guarantee). Fires on any local generation increase — including a cross-tab
// adoption — which is harmless: the worker closes on the first higher-generation message, so
// multiple tabs announcing the same commit is idempotent. Uses a dedicated native channel, distinct
// from this atom's own sync channel, so there is no echo loop.
const retireSupersededWorkersEffect: AtomEffect<GenerationState> = ({ onSet }) => {
  onSet((newValue, oldValue) => {
    const previousGeneration =
      oldValue instanceof DefaultValue || oldValue == null ? 0 : oldValue.generation;
    if (newValue.generation > previousGeneration) {
      broadcastGenerationCommit(newValue.generation);
    }
  });
};

// VX-4302: Recoil state for the worker-generation escape hatch.

export const DEFAULT_GENERATION_STATE: GenerationState = {
  generation: 0,
  lastBumpAt: 0,
  backoffLevel: 0,
};

// VX-4302: the generation persists in localStorage across sessions so a returning window connects to
// the live worker.
//
// VX-4834 follow-up (#9512 discussion_r3722846263): backoffLevel/lastBumpAt are ALSO restored now, but
// from sessionStorage via escalationStore, not from here. This comment used to say they must never be
// persisted, on the grounds that a fresh session would inherit the previous session's escalation — the
// degraded banner showing on a first stall, the cooldown-free first bump skipped. That reasoning holds
// for localStorage, which is what "persisted" meant when it was written; it does not hold for a
// per-tab, incident-length record. A genuinely fresh session is a new tab, which shares no
// sessionStorage and still restores { generation, backoffLevel: 0, lastBumpAt: 0 }.
//
// What the old reset-on-load was actually destroying is a RELOAD inside one live incident, which under
// the worklist navigation pattern is every case-open: backoffLevel was 0 every time, so cooldownMs(0)
// made canBump unconditionally true and backoffLevel could never climb to BACKOFF_CEILING, leaving
// bumps unpaced and escapeHatchDegradedAtom permanently unset. The full state is still coordinated
// across live tabs by broadcastChannelSynchronizerEffect.
export const GENERATION_STORAGE_KEY = 'viewer.escapeHatch.generation';

// VX-4363: non-Recoil reader for by-name control-message callers that must connect to the live
// generation but cannot go through the atom — either because they render without a RecoilRoot
// (ResetCachePage) or because they are plain classes with no React tree (Ward's WorkerListener).
// Mirrors persistGenerationOnlyEffect's parsing so a cold read agrees with the atom's own hydration.
//
// Takes the caller's own FF.VIEWER_SW_GENERATION_BUMP read as a parameter (rather than reading the
// flag itself) and forces 0 when it's false, mirroring selectGeneration's contract. This matters
// beyond the "flag never turned on" case the ticket originally called out: if the flag was ON long
// enough for a bump to persist and is later turned back off (kill-switch/rollback), the live worker
// (via selectGeneration in useOpenPixelDataSharedWorker) reconnects at generation 0, but a stale
// generation would otherwise still sit in localStorage. Without this gate these callers would target
// a dead worker name instead of the actual live gen-0 one — reintroducing the exact bug this ticket
// fixes, just via a flag-rollback trigger instead of a flag-rollout one.
export function readPersistedGeneration(generationBumpEnabled: boolean): number {
  if (!generationBumpEnabled) return 0;
  try {
    const stored = localStorage.getItem(GENERATION_STORAGE_KEY);
    const generation = stored == null ? 0 : Number.parseInt(stored, 10);
    return Number.isFinite(generation) && generation > 0 ? generation : 0;
  } catch {
    return 0;
  }
}

// VX-4363: collapses the "read FF.VIEWER_SW_GENERATION_BUMP, then readPersistedGeneration" pair that
// was otherwise duplicated at every by-name control-message call site (ResetCachePage, RefreshButton,
// useAuth logout, useClearStoredDataOnClose, Ward's ActionButtons) into one hook.
export function useLiveWorkerGeneration(): number {
  const [generationBumpEnabled] = useFeatureFlagEnabled(FF.VIEWER_SW_GENERATION_BUMP);
  return readPersistedGeneration(generationBumpEnabled);
}

const persistGenerationOnlyEffect: AtomEffect<GenerationState> = ({ setSelf, onSet }) => {
  // Escalation is read first so it can be folded into whichever branch below sets self. A null read
  // (nothing stored, expired incident, unreadable storage) degrades to DEFAULT_GENERATION_STATE's
  // zeros, i.e. exactly the behaviour that shipped before this was durable.
  const escalation = readEscalation({ tabId: TAB_ID, now: Date.now() }) ?? {
    backoffLevel: DEFAULT_GENERATION_STATE.backoffLevel,
    lastBumpAt: DEFAULT_GENERATION_STATE.lastBumpAt,
  };
  try {
    const stored = localStorage.getItem(GENERATION_STORAGE_KEY);
    const generation = stored == null ? 0 : Number.parseInt(stored, 10);
    if (Number.isFinite(generation) && generation > 0) {
      setSelf({ ...DEFAULT_GENERATION_STATE, generation, ...escalation });
    } else if (escalation.backoffLevel > 0 || escalation.lastBumpAt > 0) {
      // Escalation without a persisted generation is reachable: the first bump of an incident writes
      // generation 1 to localStorage, but a tab can also accrue escalation while still at generation
      // 0 if a bump was proposed and the write failed. Restore it rather than silently dropping it.
      setSelf({ ...DEFAULT_GENERATION_STATE, ...escalation });
    }
  } catch {
    // localStorage unavailable (privacy mode / disabled) — keep the default generation 0.
  }

  onSet((newValue, oldValue) => {
    try {
      if (newValue instanceof DefaultValue) {
        localStorage.removeItem(GENERATION_STORAGE_KEY);
        clearEscalation({ tabId: TAB_ID });
        return;
      }
      const previousGeneration =
        oldValue instanceof DefaultValue || oldValue == null ? undefined : oldValue.generation;
      if (newValue.generation !== previousGeneration) {
        localStorage.setItem(GENERATION_STORAGE_KEY, String(newValue.generation));
      }
      // Mirror the escalation so the next page inherits the cooldown and the climb toward the
      // ceiling. Cleared rather than written at level 0, which is the wound-down state after
      // delivery is restored — leaving a level-0 record would just age out to the same thing.
      if (newValue.backoffLevel > 0 || newValue.lastBumpAt > 0) {
        writeEscalation({
          tabId: TAB_ID,
          backoffLevel: newValue.backoffLevel,
          lastBumpAt: newValue.lastBumpAt,
          now: Date.now(),
        });
      } else {
        clearEscalation({ tabId: TAB_ID });
      }
    } catch {
      // ignore write failures
    }
  });
};

// Durable generation + cross-tab. The generation persists across reloads/sessions so a returning
// window connects to the live worker; the escalation (backoffLevel/lastBumpAt) is session-scoped and
// deliberately NOT persisted (see persistGenerationOnlyEffect). broadcastChannelSynchronizerEffect
// syncs the full state across live tabs; the mergeFn keeps cross-tab convergence idempotent and
// monotonic — two tabs proposing the same next generation converge there, and a stale lower
// generation is ignored.
export const generationStateAtom: RecoilState<GenerationState> = atom<GenerationState>({
  key: 'viewer.escapeHatch.generationState',
  default: DEFAULT_GENERATION_STATE,
  effects: [
    broadcastChannelSynchronizerEffect<GenerationState>({
      mergeFn: (oldValue, newValue) =>
        oldValue == null ? newValue : adoptGeneration(oldValue, newValue),
    }),
    persistGenerationOnlyEffect,
    retireSupersededWorkersEffect,
  ],
});

// VX-4302 #8: per-window UI signal — true once bumps have escalated to the backoff ceiling and
// delivery is still down. Local (not synced or persisted); drives the ambient degraded indicator,
// and clears the moment a frame is delivered.
export const escapeHatchDegradedAtom: RecoilState<boolean> = atom<boolean>({
  key: 'viewer.escapeHatch.degraded',
  default: false,
});

// VX-4302: the manual override behind the "Restart imaging delivery" affordance. Commits a bump
// directly, bypassing quorum and cooldown — the human's strongest recovery, deliberately
// independent of the automated ladder so it still works if that logic is ever wrong.
export function useForceWorkerGenerationBump(): () => void {
  // useRecoilCallback reads the current generation from the snapshot at call time, so there is no
  // need to subscribe to or mirror the atom in a ref.
  return useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        const current = snapshot.getLoadable(generationStateAtom).getValue();
        const next = proposeBump(current, Date.now());
        set(generationStateAtom, next);
        analytics.track(viewer.sys.escapeHatchBump, {
          reason: 'manual',
          generation: next.generation,
        });
      },
    []
  );
}
